import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import BasicPage from "./BasicPage/BasicPage";
import ApproverPage from "./ApproverPage/ApproverPage";
import VoterPage from "./VoterPage/VoterPage";
import QuestionTabs, { questionTab, VERSIONS_TAB } from "./Versions/QuestionTabs";
import QuestionVersions from "./Versions/QuestionVersions";
import QuestionTopBar, { QuestionPageState } from "./review/QuestionTopBar";
import { BookletLockNotice, questionBooklets } from "./review/bookletLock";
import { reviewSteps } from "./review/reviewUtils";
import useQuestionPage from "./review/useQuestionPage";
import { UserContext } from "../../utils/contexts/UserContext";
import { getQuestionFolder } from "../../services/questionService";
import { canDecideIn, canReviewIn, isAdminUser } from "../../utils/folders";

// What the page lets the user do with the question: read it, vote on it, or decide its status.
const READ = 0;
const VOTE = 1;
const DECIDE = 2;

// The global permissions: outside folders, as before folders.
const permsLevel = (perms) =>
    perms.includes("approve_question") ? DECIDE : perms.includes("vote_question") ? VOTE : READ;
// An older backend's question in a folder's subject: the role there (an expert approves or sends back for changes
// too).
// An older backend without actions: a subject's moderators and admins decide; its experts read the question and vote.
const roleLevel = (role) => (canDecideIn(role) ? DECIDE : READ);

// The width of the question pages: the tab bar and the versions line up with them.
const PAGE_WIDTH = "mx-auto w-full max-w-5xl 2xl:max-w-screen-2xl";

// Puts the focus on `target()` once no dialog is left, if it fell to the page by then: Headless UI gives the focus
// back to the control that opened a dialog once the dialog has faded out, but not when that control has gone.
function focusWhenSettled(target) {
    let frames = 0;
    const step = () => {
        if (document.querySelector('[role="dialog"]') && frames < 90) {
            frames += 1;
            requestAnimationFrame(step);
            return;
        }
        const active = document.activeElement;
        if (!active || active === document.body) target()?.focus();
    };
    requestAnimationFrame(step);
}

// The user's role in the subject folder `folderUuid` (none to read without it), read with the folder. A folder not
// given to the user is no role there; a failure that may pass is `error`, and `retry` reads the folder again.
function useFolderRole(folderUuid) {
    const [read, setRead] = useState({ folder: null, role: null, error: null });
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!folderUuid) return undefined;
        const controller = new AbortController();
        getQuestionFolder(folderUuid, { signal: controller.signal })
            .then((folder) => setRead({ folder: folderUuid, role: folder?.role || null, error: null }))
            .catch((error) => {
                if (controller.signal.aborted) return;
                const refused = [403, 404].includes(error?.response?.status);
                setRead({ folder: folderUuid, role: null, error: refused ? null : error });
            });
        return () => controller.abort();
    }, [folderUuid, attempt]);

    const retry = useCallback(() => {
        setRead({ folder: null, role: null, error: null });
        setAttempt((n) => n + 1);
    }, []);
    const current = !!folderUuid && read.folder === folderUuid;
    return { role: current ? read.role : null, known: current && !read.error, error: current ? read.error : null, retry };
}

export default function Main() {
    const { user } = useContext(UserContext);
    const { uuid } = useParams();
    // The question (the default) or its versions (?tab=versions): in the address, so a reload keeps the tab.
    const [searchParams] = useSearchParams();
    const tab = questionTab(searchParams);
    const perms = user?.perms || [];
    // The question as its page shows it, read once for the page and its choice; a voter gets their own vote with it.
    const { question, loadError, history, historyLoading, historyFailed, reload } = useQuestionPage(uuid, {
        evaluator: permsLevel(perms) === VOTE ? user?.uuid : undefined,
    });

    // An older backend sends no `actions`: in a folder's subject the user's role there decides. Only admins and
    // members of some subject have one; for anyone else every question they can open is outside folders.
    const usesFolderRoles = isAdminUser(user) || (user?.folder_roles || []).length > 0;
    const legacyFolder = question && !question.actions && usesFolderRoles ? question.folder?.uuid || null : null;
    const folder = useFolderRole(legacyFolder);
    // Who likes or dislikes the question: the server says (actions.vote); one that does not, the role in the
    // question's subject (its experts, moderators and admins) or, outside folders, vote_question. Not one's own.
    const voteUnknown = !!question?.actions && question.actions.vote === undefined;
    const voteFolder = useFolderRole(voteUnknown && question?.folder ? question.folder.uuid : null);
    const ownQuestion = !!question?.author?.uuid && question.author.uuid === user?.uuid;
    const canVote = !question
        ? false
        : !voteUnknown && question.actions
          ? !!question.actions.vote
          : !ownQuestion && (question.folder ? isAdminUser(user) || canReviewIn(voteFolder.role) : permsLevel(perms) === VOTE);

    // The page goes by the question itself: whoever its actions give a step decides (in a subject its moderators and
    // admins, its experts only vote; outside folders its controllers; nobody while an active BTS booklet prints it), and
    // outside folders a vote_question user votes. An older backend: the role in the question's subject, else the
    // global permissions. Null while unknown.
    let level = null;
    if (question) {
        if (question.actions) {
            if (reviewSteps(question).transitions.length) level = DECIDE;
            else level = !question.folder && permsLevel(perms) === VOTE ? VOTE : READ;
        } else if (legacyFolder) {
            level = folder.known ? roleLevel(folder.role) : null;
        } else {
            level = permsLevel(perms);
        }
    }
    // Chosen once per question: a decision that leaves the reviewer no further step (an expert's refusal) keeps
    // their page, whose panel then says so, instead of swapping it for another one under them.
    const [chosen, setChosen] = useState({ uuid: null, level: null });
    if (level !== null && chosen.uuid !== uuid) setChosen({ uuid, level });
    const pageLevel = chosen.uuid === uuid ? chosen.level : level;

    // An active BTS booklet prints the question: said up front, and nothing on the page offers to change it.
    const booklets = questionBooklets(question);
    const locked = booklets.length > 0;
    // A booklet activated while the page was open, found by a change the user tried (a delete): the page read the
    // question again and the buttons that had the focus are gone, so the lock notice that came up takes it (a focus
    // still somewhere, e.g. on a decision's refusal, stays).
    const noticeRef = useRef(null);
    const lockOf = useRef({ uuid: null, locked: false });
    useEffect(() => {
        if (!question) return;
        const before = lockOf.current;
        lockOf.current = { uuid: question.uuid, locked };
        if (locked && !before.locked && before.uuid === question.uuid) focusWhenSettled(() => noticeRef.current);
    }, [question, locked]);

    if (!user || !user.perms) return <QuestionPageState />;
    if (!question) return <QuestionPageState error={loadError} onRetry={reload} />;
    // Never the reading page in a reviewer's place: until the role is read the page loads, or says it could not
    // read it and offers to try again.
    if (pageLevel === null) return <QuestionPageState error={folder.error} onRetry={folder.retry} />;

    const props = {
        question,
        history,
        historyLoading,
        historyFailed,
        reload,
        folderRole: folder.role,
        inFolder: !!question.folder,
        canVote,
    };
    const page =
        pageLevel === DECIDE ? (
            <ApproverPage {...props} />
        ) : pageLevel === VOTE ? (
            <VoterPage {...props} />
        ) : (
            <BasicPage {...props} />
        );
    // Versions came with the review: that backend's detail always has `version` (null for a question older than
    // them). An older one keeps none, so it gets no tab for them.
    const withVersions = "version" in question;
    const versionsOpen = withVersions && tab === VERSIONS_TAB;
    return (
        <div className="space-y-6">
            {/* One top bar for both tabs: Back, the tabs, the guide's "?", and on the question tab its actions. */}
            <QuestionTopBar
                className={PAGE_WIDTH}
                question={question}
                folderRole={folder.role}
                onReload={reload}
                tabs={withVersions ? <QuestionTabs active={tab} /> : null}
                actions={!versionsOpen}
            />
            {!versionsOpen && locked && <BookletLockNotice ref={noticeRef} booklets={booklets} className={PAGE_WIDTH} />}
            {/* Kept while the Versions tab is open: a decision or a comment being written stays. */}
            <div hidden={versionsOpen}>{page}</div>
            {versionsOpen && <QuestionVersions key={uuid} uuid={uuid} className={PAGE_WIDTH} />}
        </div>
    );
}
